import { PlusOutlined } from "@ant-design/icons";
import { useCallback, useMemo, type FC } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { Button } from "../../../shared";
import {
  createEmptyMonthlyChallenge,
  duplicateMonthlyChallenge,
  reorderMonthlyChallenges,
  type MonthlyChallengeDraft,
  type MonthlyChallengesValues,
} from "../monthlyChallengeData";
import MonthlyChallengeCard, {
  type MonthlyChallengeCardDragProps,
} from "./MonthlyChallengeCard";

import styles from "./MonthlyChallengesStep.module.css";

export type MonthlyChallengesStepProps = {
  values: MonthlyChallengesValues;
  onChange: (next: MonthlyChallengesValues) => void;
};

/**
 * Step 2 of the Create Monthly Challenge wizard. Renders the challenge list and
 * the "Add Challenge" affordances; each challenge is edited inline via
 * {@link MonthlyChallengeCard}. Fully controlled — every mutation flows back to
 * the parent wizard through `onChange`.
 */
const MonthlyChallengesStep: FC<MonthlyChallengesStepProps> = ({
  values,
  onChange,
}) => {
  const { challenges } = values;

  // Only one challenge may be edited at a time — an open, never-saved draft
  // blocks adding another (mirrors the Season wizard). A saved challenge that
  // has been re-opened doesn't count, so it can always be collapsed again.
  const hasUnsavedDraft = useMemo(
    () => challenges.some((challenge) => challenge.isExpanded && !challenge.isSaved),
    [challenges],
  );

  const handleAddChallenge = () => {
    if (hasUnsavedDraft) return;
    onChange({
      ...values,
      challenges: [...challenges, createEmptyMonthlyChallenge()],
    });
  };

  const handleChallengeChange = (
    index: number,
    next: MonthlyChallengeDraft,
  ) => {
    onChange({
      ...values,
      challenges: challenges.map((challenge, i) =>
        i === index ? next : challenge,
      ),
    });
  };

  const updateChallengeAt = (
    index: number,
    updater: (challenge: MonthlyChallengeDraft) => MonthlyChallengeDraft,
  ) => {
    onChange({
      ...values,
      challenges: challenges.map((challenge, i) =>
        i === index ? updater(challenge) : challenge,
      ),
    });
  };

  const handleChallengeSave = (index: number) => {
    updateChallengeAt(index, (challenge) => ({
      ...challenge,
      isSaved: true,
      isExpanded: false,
    }));
  };

  const handleChallengeEdit = (index: number) => {
    updateChallengeAt(index, (challenge) => ({
      ...challenge,
      isExpanded: true,
    }));
  };

  const handleChallengeClose = (index: number) => {
    const target = challenges[index];
    if (!target) return;
    // An unsaved draft is discarded on close; a previously-saved challenge just
    // collapses back to its summary.
    if (!target.isSaved) {
      onChange({
        ...values,
        challenges: challenges.filter((_, i) => i !== index),
      });
      return;
    }
    updateChallengeAt(index, (challenge) => ({
      ...challenge,
      isExpanded: false,
    }));
  };

  const handleDuplicate = (index: number) => {
    // A copy opens as a new unsaved draft, so block it while one is already
    // open — same invariant the Add Challenge buttons enforce.
    if (hasUnsavedDraft) return;
    onChange({
      ...values,
      challenges: challenges.flatMap((challenge, i) =>
        i === index
          ? [
              challenge,
              duplicateMonthlyChallenge(
                challenge,
                AppStrings.CHALLENGE_DUPLICATE_NAME_SUFFIX,
              ),
            ]
          : [challenge],
      ),
    });
  };

  const handleDelete = (index: number) => {
    onChange({
      ...values,
      challenges: challenges.filter((_, i) => i !== index),
    });
  };

  // Two sensors matching the Season pattern: pointer for mouse/touch (with a
  // small activation distance so a click on the handle isn't read as a drag)
  // and keyboard for accessible reordering.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // dnd-kit caches its registration against this reference, so keep it stable
  // across renders where the challenge ids haven't changed.
  const challengeIds = useMemo(
    () => challenges.map((challenge) => challenge.id),
    [challenges],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      // No-op on a drop outside a sortable item or back onto the origin slot.
      if (!over || active.id === over.id) return;
      const next = reorderMonthlyChallenges(
        challenges,
        String(active.id),
        String(over.id),
      );
      if (next === challenges) return;
      // Local reorder only — there is no persistence API yet, so the new
      // order lives entirely in wizard state.
      onChange({ ...values, challenges: next });
    },
    [challenges, onChange, values],
  );

  return (
    <div className={styles.step} data-testid="monthly-challenges-step">
      <div className={styles.intro}>
        <div className={styles.introText}>
          <h2 className={styles.heading}>{AppStrings.CREATE_CHALLENGES}</h2>
          <p className={styles.description}>
            {AppStrings.CREATE_CHALLENGES_DESCRIPTION}
          </p>
        </div>
        <Button
          intent="primary"
          onClick={handleAddChallenge}
          disabled={hasUnsavedDraft}
          title={
            hasUnsavedDraft
              ? AppStrings.ADD_CHALLENGE_FINISH_DRAFT_FIRST
              : undefined
          }
          data-testid="monthly-challenges-add-top"
        >
          {AppStrings.ADD_CHALLENGE}
        </Button>
      </div>

      {challenges.length > 0 ? (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={challengeIds}
              strategy={verticalListSortingStrategy}
            >
              <ul
                className={styles.list}
                aria-label={AppStrings.MONTHLY_CHALLENGE_LIST_ARIA}
              >
                {challenges.map((challenge, index) => (
                  <SortableMonthlyChallengeCard
                    key={challenge.id}
                    challenge={challenge}
                    index={index}
                    onChange={(next) => handleChallengeChange(index, next)}
                    onDuplicate={() => handleDuplicate(index)}
                    onDelete={() => handleDelete(index)}
                    onSave={() => handleChallengeSave(index)}
                    onEdit={() => handleChallengeEdit(index)}
                    onClose={() => handleChallengeClose(index)}
                    disableDuplicate={hasUnsavedDraft}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>

          <button
            type="button"
            className={styles.addChallengeButton}
            onClick={handleAddChallenge}
            disabled={hasUnsavedDraft}
            title={
              hasUnsavedDraft
                ? AppStrings.ADD_CHALLENGE_FINISH_DRAFT_FIRST
                : undefined
            }
            data-testid="monthly-challenges-add-bottom"
          >
            <PlusOutlined className={styles.addChallengeIcon} />
            {AppStrings.ADD_CHALLENGE}
          </button>
        </>
      ) : null}
    </div>
  );
};

export default MonthlyChallengesStep;

type SortableMonthlyChallengeCardProps = {
  challenge: MonthlyChallengeDraft;
  index: number;
  onChange: (next: MonthlyChallengeDraft) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onSave: () => void;
  onEdit: () => void;
  onClose: () => void;
  disableDuplicate: boolean;
};

/**
 * Wraps {@link MonthlyChallengeCard} so it participates in the parent
 * `<SortableContext>`. Derives the transform style + activator wiring from
 * `useSortable` and hands them to the card as `drag`, keeping the card itself
 * free of any `@dnd-kit/*` imports.
 */
const SortableMonthlyChallengeCard: FC<SortableMonthlyChallengeCardProps> = ({
  challenge,
  ...rest
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: challenge.id });

  const drag: MonthlyChallengeCardDragProps = {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transformStyle: {
      transform: CSS.Transform.toString(transform),
      transition,
    },
    isDragging,
  };

  return (
    <li className={styles.listItem}>
      <MonthlyChallengeCard challenge={challenge} drag={drag} {...rest} />
    </li>
  );
};
